import { AxiosError } from 'axios';
import { LOCAL_STORAGE_KEY_REDIRECT_URL, LOCAL_STORAGE_KEY_USER_ID, setLocalStorage } from '../../../../contexts';
import { showUpgradeModal } from '../../../upgradeService';

type ErrorResponseData = string | { message?: string; errors?: Record<string, string[]>; };

const parseErrorData = (data: ErrorResponseData | undefined): string => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  if (data?.errors) {
    const msgs: string[] = [];
    for (const key of Object.keys(data.errors)) {
      msgs.push(...data.errors[key]);
    }
    return msgs.join('. ');
  }
  return '';
};

export const errorInterceptor = (error: AxiosError) => {
  if (error.message === 'Network Error') {
    return Promise.reject(new Error('Erro de conexão.'));
  }

  if (error.response?.status === 401) {
    const storedId = localStorage.getItem(LOCAL_STORAGE_KEY_USER_ID);
    // null === no stored ID (fresh visit, never logged in)
    // '0' === cleared by previous setLocalStorage() — user WAS logged in
    // Treat '0' as not-authenticated to avoid a second redirect loop when
    // the page reloads: if localStorage was cleared (ID='0'), the user no
    // longer has a session to protect — just let LoginPage gate the view.
    const wasAuthenticated = storedId !== null && storedId !== '0';
    setLocalStorage();

    localStorage.setItem(LOCAL_STORAGE_KEY_REDIRECT_URL, window.location.pathname + window.location.search);

    const pagesToNotRedirect = ['/', '/create-business-trial', '/subscription/checkout', '/subscription/success', '/subscription/pending'];

    if (!pagesToNotRedirect.includes(window.location.pathname) && wasAuthenticated) {
      // Redirect to /home?expired=1 to force a clean login state.
      // /home was historically excluded from redirect, but UserContext now
      // skips getUserFromCookie when there's no cached session (user.id === 0),
      // so the redirect loop can no longer occur. The ?expired=1 param is
      // kept for backwards compatibility.
      window.location.href = window.location.origin + '/home?expired=1';
    }
  }

  if (error.response?.status === 402) {
    // Subscription expired/overdue — show upgrade modal
    const data = error.response.data as ErrorResponseData;
    const message = parseErrorData(data) || 'Sua assinatura está expirada. Renove para continuar.';
    // Extract businessId from response data (included by middleware)
    const responseData = error.response.data as Record<string, unknown> | undefined;
    const businessId = typeof responseData?.businessId === 'number' ? responseData.businessId : undefined;
    const isOverdue = message.toLowerCase().includes('vencida');
    showUpgradeModal(undefined, message, businessId, isOverdue);
  }

  if (error.response?.status === 403) {
    const data = error.response.data as ErrorResponseData;
    const message = parseErrorData(data);

    // Feature flag denial — show upgrade modal with feature name
    if (message.toLowerCase().includes('plano') || message.toLowerCase().includes('upgrade') || message.toLowerCase().includes('assinatura')) {
      // Extract feature info from message
      const featureMatch = message.match(/não inclui[\s\S]*?([^.!?]+)/i);
      // Extract businessId from response
      const responseData = error.response.data as Record<string, unknown> | undefined;
      const businessId = typeof responseData?.businessId === 'number' ? responseData.businessId : undefined;
      showUpgradeModal(featureMatch?.[1]?.trim() || 'esta funcionalidade', undefined, businessId);
      return Promise.reject(new Error(message));
    }

    // Authorization 403 — redirect to forbidden
    window.location.href = window.location.origin + '/forbidden';
  }

  const apiMessage = error.response?.data as ErrorResponseData | undefined;
  const finalMessage = parseErrorData(apiMessage) || error.message || 'Erro inesperado, contate o administrador.';

  return Promise.reject(new Error(finalMessage));
};